import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { DeliveryArea } from "@components/address/func/isAddressDeliverable";

export interface DeliverySlot {
  delivery_date: string;
  is_closed_all_day: boolean;
  slots: {
    slot_id: number;
    slot_source: string;
    start_time: string;
    end_time: string;
    available_capacity?: number;
  }[];
}

export interface Product {
  line_id?: string;
  gas_type?: string;
  unit_price: number;
  qty: number;
  totalPrice?: number;
  kilogram?: number;
  display_name?: string;
  // 對齊 BE GasItemReqDto / AppDraftGasItem flat shape（usage_location + note + floor + is_elevator）
  customer_delivery_id?: number | null;
  usage_location: string;
  note?: string | null;
  floor: number;
  is_elevator: boolean;
  // 商品分類用欄位（pricing pipeline）
  type?: "gas" | "cis_gas";
  price_id?: number;
  gas_id?: number;
  replacing_customer_delivery_id?: number | null;
  replacing_spec?: string | null;
}

export interface DeliveryTime {
  type: "scheduled" | "immediate";
  date: string | null;
  slotId: number | null;
  slotSource: string | null;
  slotLabel: string | null;
}

/**
 * 下單流程「付款方式」步驟的選擇（/gasShop/payment）。
 * - credit_card：已綁卡 id，送單時 payment_method_ref 帶該 id
 * - electronic：電子支付品牌（Apple Pay / LINE Pay），送單時 payment_method_ref 帶品牌字面量
 * - cash_on_delivery / monthly：維持原本三態 fallback（送 cash_on_delivery）
 */
/** 電子支付品牌（送單 payment_method_ref 直接帶這個字面量；與 app-api WALLET_PAYMENT_METHODS 對齊）。 */
export type WalletBrand = "apple_pay" | "line_pay" | "ipass_money";

export type PaymentSelection =
  | { type: "cash_on_delivery" }
  | { type: "monthly" }
  | { type: "credit_card"; paymentMethodId: string; label: string }
  | { type: "electronic"; brand: WalletBrand; label: string };

interface ProductStore {
  orderState: string; //-- 訂單狀態 ; success 成功抓取最新購物車資訊 ; loading 載入中
  setOrderState: (state: string) => void;
  draftOrderId: null | string; //-- 草稿訂單 id
  setDraftOrderId: (id: string) => void;
  cartProductTotalPrice: number; //-- 購買商品總額
  chooseProduct: Product | null; //-- 選擇商品 (單一)
  setChooseProduct: (product: Product) => void;
  clearChooseProduct: () => void;
  productList: Product[]; //-- 購買商品列表 (購物車)
  setProductList: (list: Product[]) => void; //-- 整筆購物車商品內容寫入
  addProduct: (product: Product) => void;
  removeProduct: (productId: string) => void;
  clearProducts: () => void;
  updateQuantity: (itemId: string, newQuantity: number) => void;
  syncDraft: (draft: any) => void;
  draft: any | null; //-- 原始草稿訂單細節
  deliverySlots: DeliverySlot[];
  setDeliverySlots: (slots: DeliverySlot[]) => void;
  // 供應商當下是否營業（立即配送可用）。公休但仍可預約其他時段時為 false。由 bootstrap 寫入。
  isOpenNow: boolean;
  setIsOpenNow: (open: boolean) => void;
  // 下一個營業時段的開始時間（bootstrap available_suppliers[].next_open_at，ISO +08:00）。
  // 非營業時點「立即配送」的提示文字用（見 formatNextOpen）。後端未提供（舊版）或找不到時為 null。
  nextOpenAt: string | null;
  setNextOpenAt: (nextOpenAt: string | null) => void;
  // 此瓦斯行是否開放「線上預約配送」（supplier.system_setting.allow_customer_reservation）。
  // 由 bootstrap 寫入；未開放時不顯示預約選項，且公休時完全不可下單。預設 false（與後端 === true 判定對齊）。
  allowReservation: boolean;
  setAllowReservation: (allow: boolean) => void;
  // 此瓦斯行是否隱藏金額（supplier.system_setting.hide_amount_on_pending）。開啟時：整個下訂流程（商品/小計/結帳訂單摘要）
  // 全程不顯示價格，客戶照常下單，金額由瓦斯行接單確認後才提供；訂單頁另在「待確認」時隱藏金額。由 bootstrap 寫入。預設 false。
  hideAmountOnPending: boolean;
  setHideAmountOnPending: (hide: boolean) => void;
  // 此瓦斯行的配送範圍（bootstrap available_suppliers[].delivery_areas）。
  // 新增地址時用來即時提示所選 city/area 是否可配送；空陣列/未提供 → 全部可送。由 bootstrap 寫入。
  deliveryAreas: DeliveryArea[];
  setDeliveryAreas: (areas: DeliveryArea[]) => void;
  // 賣方身分（瓦斯行名稱／統編／電話），由 bootstrap 寫入，供下單流程的版頭顯示「這單跟誰買」。
  // taxId / phone 缺漏時為 null（呼叫端整段隱藏，不顯示「統編 --」）。
  // 與 deliveryAreas 同屬「當前 bootstrap 回應」的暫態資料 → 不 persist，避免換瓦斯行後殘留舊賣方。
  supplierIdentity: SupplierIdentity | null;
  setSupplierIdentity: (identity: SupplierIdentity | null) => void;
  deliveryTime: DeliveryTime;
  setDeliveryTime: (deliveryTime: DeliveryTime) => void;
  // 電子付款開關（supplier platform_setting.enable_electronic_payment，PW 後台控制）。由 bootstrap 寫入。
  // 開啟時下單流程在配送時間之後多一步「付款方式」；關閉時跳過該步、送單一律貨到付款／月結。
  allowElectronicPay: boolean;
  setAllowElectronicPay: (allow: boolean) => void;
  // 月結客戶（cis.customer_type === 月結客戶）→ 付款方式步驟的現金選項顯示「月結」。由 bootstrap 寫入。
  isMonthlyCustomer: boolean;
  setIsMonthlyCustomer: (monthly: boolean) => void;
  // 走表客戶（一般瓦斯表／物聯網瓦斯表／物聯網發報器）→ 依抄表度數結算，下單當下金額未定，
  // 不走線上收款、不顯示付款方式那一步。由 bootstrap 寫入。
  isMeteredCustomer: boolean;
  setIsMeteredCustomer: (metered: boolean) => void;
  // 付款方式步驟的選擇；null = 尚未選（電子付款開啟時結帳頁不放行）。送單成功後隨購物車一起清空。
  paymentSelection: PaymentSelection | null;
  setPaymentSelection: (selection: PaymentSelection | null) => void;
  customerCouponId: string | null;
  setCustomerCouponId: (id: string | null) => void;
  // 從優惠券頁「點券去下單」帶過來的待套用券：與草稿同步無關，購物車載入後消費一次 → 寫入 customerCouponId。
  pendingCouponId: string | null;
  setPendingCouponId: (id: string | null) => void;
  // preview 回傳的優惠券折扣總額；供「訂購項目」與「訂單摘要」一致地把折扣折在瓦斯價上。
  // null = 尚未由 preview 解析（pending），number = 已解析（0 代表確定無折扣）。
  // 用 null 區分「pending」與「resolved 0」，讓品項列在折扣算出前顯示 loading 而非閃一下原價。
  couponDiscount: number | null;
  setCouponDiscount: (amount: number | null) => void;
  // 本張訂單要折抵的瓦斯帳戶點數（點）。折抵順序：優惠券（後端 preview 算）→ 存氣點數 → 帳戶點數。
  // 存氣點數只折得到瓦斯品項、帳戶點數可折全單；實際可折上限由 calcPointsRedeem 依當下金額 clamp，
  // 所以購物車異動時不必歸零（會自動縮到新上限），只有清空購物車才重置。
  pointsRedeem: PointsRedeem;
  setPointsRedeem: (redeem: PointsRedeem) => void;
  // 「隱藏金額」的訂單（瓦斯行接單後才報價）折抵不出金額，只收意願：客戶勾了哪幾種點數，
  // 由瓦斯行在定價時套用。與 pointsRedeem 分開存，因為兩者語意不同 ——
  // 一個是「折這麼多點」，一個是「有金額後幫我折」。
  pointsIntent: PointsIntent;
  setPointsIntent: (intent: PointsIntent) => void;
  // preview 回傳的「券後應付金額」（含費用、樓層費、平台服務費，尚未折點數）。
  // 由 GasOrderSummary 取得 preview 後寫入，讓點數折抵區塊與訂單摘要用同一個基準算折抵上限。
  // null = preview 尚未回。
  previewTotal: number | null;
  setPreviewTotal: (amount: number | null) => void;
}

/** 點數折抵選擇（單位：點，非金額）。 */
export interface PointsRedeem {
  storedGas: number;
  account: number;
}

/** 隱藏金額的訂單上「要不要折」的意願（沒有金額可算，只有勾或不勾）。 */
export interface PointsIntent {
  storedGas: boolean;
  account: boolean;
}

const NO_POINTS_REDEEM: PointsRedeem = { storedGas: 0, account: 0 };
/**
 * 意願預設「開」。
 *
 * 這兩種點數本來就是客戶自己的錢（溢繳／儲值）與氣（退桶剩餘），放著不折沒有任何好處；
 * 而這種訂單看不到金額，要客戶為了一件對他只有利的事多按一下開關並不合理。
 * 想留著不用的人再關掉即可（關掉會記住到這張單結束，清空購物車後回到預設）。
 *
 * 注意這與「一般訂單」的折抵不同：那裡預設不折，因為金額看得見、折多少是當下的決定。
 */
const DEFAULT_POINTS_INTENT: PointsIntent = { storedGas: true, account: true };

/** 賣方身分：下單流程版頭顯示的瓦斯行名稱／統編／電話。 */
export interface SupplierIdentity {
  name: string;
  taxId: string | null;
  phone: string | null;
}

const calculateTotal = (list: Product[]) => {
  return list.reduce((sum, item) => {
    const price = Number(item.totalPrice) || 0;
    return sum + price;
  }, 0);
};

export const useOrder = create<ProductStore>()(
  persist(
    (set) => ({
      orderState: "loading",
      setOrderState: (state: string) => set({ orderState: state }),
      draftOrderId: null,
      setDraftOrderId: (id: string) => set({ draftOrderId: id }),
      productList: [],
      setProductList: (list: Product[]) =>
        set(() => ({
          productList: list,
          cartProductTotalPrice: list.reduce((sum: number, item: Product) => Number(sum) + Number(item.totalPrice || 0), 0),
        })),
      draft: null,
      syncDraft: (draft: any) => {
        if (!draft) return;

        // user 在 cart UI 選的時段尚未 PATCH 到 draft 前，draft.delivery_date 會是 null。
        // 此時若有其他 API（如 TopBarTools 因 context_id 變動再 GET draft、handleUpdateQuantity 等）
        // 觸發 syncDraft，會把本地剛選好的時段覆寫成 null。只有 draft 真的帶值時才覆蓋本地。
        const hasDraftDeliveryDate = !!draft.delivery_date;

        const mapItem = (item: any) => {
          const rawHasElevator = item.is_elevator;
          const isElevator =
            typeof rawHasElevator === "boolean" ? rawHasElevator : rawHasElevator === "Y" ? true : rawHasElevator === "N" ? false : !!rawHasElevator;

          return {
            ...item,
            totalPrice: item.unit_price * item.qty,
            customer_delivery_id: item.customer_delivery_id ?? null,
            usage_location: item.usage_location ?? "",
            note: item.note ?? null,
            floor: typeof item.floor === "number" ? item.floor : Number(item.floor) || 0,
            is_elevator: isElevator,
            replacing_customer_delivery_id: item.replacing_customer_delivery_id ?? null,
            replacing_spec: item.replacing_spec ?? null,
          };
        };

        const mappedGasList = (draft.gas_list || []).map(mapItem);
        const mappedCommodityList = (draft.commodity_list || []).map(mapItem);
        const mappedList = [...mappedGasList, ...mappedCommodityList];

        set((state) => ({
          draft,
          draftOrderId: draft.draft_id,
          productList: mappedList,
          cartProductTotalPrice: calculateTotal(mappedList),
          orderState: "success",
          deliveryTime: hasDraftDeliveryDate
            ? {
                type: draft.delivery_type === "immediate" ? "immediate" : "scheduled",
                date: draft.delivery_date,
                slotId: draft.delivery_slot_id,
                slotSource: draft.delivery_slot_source,
                slotLabel: draft.delivery_slot_label,
              }
            : state.deliveryTime,
          customerCouponId: draft.customer_coupon_id || null,
        }));
      },
      deliverySlots: [],
      setDeliverySlots: (slots: DeliverySlot[]) => set({ deliverySlots: slots }),
      isOpenNow: true,
      setIsOpenNow: (open: boolean) => set({ isOpenNow: open }),
      nextOpenAt: null,
      setNextOpenAt: (nextOpenAt: string | null) => set({ nextOpenAt }),
      allowReservation: false,
      setAllowReservation: (allow: boolean) => set({ allowReservation: allow }),
      hideAmountOnPending: false,
      setHideAmountOnPending: (hide: boolean) => set({ hideAmountOnPending: hide }),
      deliveryAreas: [],
      setDeliveryAreas: (areas: DeliveryArea[]) => set({ deliveryAreas: areas }),
      supplierIdentity: null,
      setSupplierIdentity: (identity: SupplierIdentity | null) => set({ supplierIdentity: identity }),
      deliveryTime: {
        type: "scheduled",
        date: null,
        slotId: null,
        slotSource: null,
        slotLabel: null,
      },
      setDeliveryTime: (deliveryTime: DeliveryTime) => set({ deliveryTime }),
      allowElectronicPay: false,
      setAllowElectronicPay: (allow: boolean) => set({ allowElectronicPay: allow }),
      isMonthlyCustomer: false,
      setIsMonthlyCustomer: (monthly: boolean) => set({ isMonthlyCustomer: monthly }),
      isMeteredCustomer: false,
      setIsMeteredCustomer: (metered: boolean) => set({ isMeteredCustomer: metered }),
      paymentSelection: null,
      setPaymentSelection: (selection: PaymentSelection | null) => set({ paymentSelection: selection }),
      customerCouponId: null,
      // 換券 / 取消券都讓折扣回到 pending（null），等下一次 preview 回來才顯示新折扣，
      // 避免短暫顯示舊券折扣或原價
      setCustomerCouponId: (id: string | null) => set({ customerCouponId: id, couponDiscount: null }),
      pendingCouponId: null,
      setPendingCouponId: (id: string | null) => set({ pendingCouponId: id }),
      couponDiscount: null,
      setCouponDiscount: (amount: number | null) => set({ couponDiscount: amount }),
      pointsRedeem: NO_POINTS_REDEEM,
      setPointsRedeem: (redeem: PointsRedeem) => set({ pointsRedeem: redeem }),
      pointsIntent: DEFAULT_POINTS_INTENT,
      setPointsIntent: (intent: PointsIntent) => set({ pointsIntent: intent }),
      previewTotal: null,
      setPreviewTotal: (amount: number | null) => set({ previewTotal: amount }),
      cartProductTotalPrice: 0,
      chooseProduct: null,
      setChooseProduct: (product: Product) => set({ chooseProduct: product }),
      clearChooseProduct: () => set({ chooseProduct: null }),
      addProduct: (product: Product) =>
        set((state) => {
          const newList = [...state.productList, product];
          return {
            productList: newList,
            cartProductTotalPrice: calculateTotal(newList),
            couponDiscount: null, // 購物車異動使折扣失效（pending），待 preview 重算
          };
        }),
      removeProduct: (productId: string) =>
        set((state) => {
          const newList = state.productList.filter((item) => item.line_id !== productId);
          return {
            productList: newList,
            cartProductTotalPrice: calculateTotal(newList),
            couponDiscount: null, // 購物車異動使折扣失效（pending），待 preview 重算
          };
        }),
      clearProducts: () =>
        set({
          productList: [],
          chooseProduct: null,
          cartProductTotalPrice: 0,
          draftOrderId: null,
          orderState: "loading",
          draft: null,
          deliveryTime: { type: "scheduled", date: null, slotId: null, slotSource: null, slotLabel: null },
          paymentSelection: null,
          customerCouponId: null,
          couponDiscount: null,
          pointsRedeem: NO_POINTS_REDEEM,
          pointsIntent: DEFAULT_POINTS_INTENT,
          previewTotal: null,
        }),
      updateQuantity: (itemId: string, newQuantity: number) =>
        set((state) => {
          const newList = state.productList.map((item) => {
            if (item.line_id === itemId) {
              const qty = Math.max(1, newQuantity);
              return {
                ...item,
                qty: qty,
                totalPrice: item.unit_price * qty,
              };
            }
            return item;
          });
          return {
            productList: newList,
            cartProductTotalPrice: calculateTotal(newList),
            couponDiscount: null, // 數量異動使折扣失效（pending），待 preview 重算
          };
        }),
    }),
    {
      name: "orderData",
      storage: createJSONStorage(() => sessionStorage),
      // deliveryAreas / supplierIdentity 屬於「當前 bootstrap 回應」的暫態資料，不應跨 supplier /
      // 頁面重整持久化：persist 下來的舊值可能屬於另一個瓦斯行（配送範圍提示錯誤、版頭顯示錯的賣方）。
      // 明確排除，其餘欄位維持既有 persist 行為（fail-open：只在啟動時清空、由各 bootstrap 重新寫入）。
      partialize: (state) => {
        const { deliveryAreas: _deliveryAreas, supplierIdentity: _supplierIdentity, ...rest } = state;
        return rest;
      },
    },
  ),
);
